import React, { useState } from 'react';
import api from '../../services/api';

const MemoryDiagnosticsTab = () => {
  const [diagnosticsResults, setDiagnosticsResults] = useState(null);
  const [isRunningDiagnostics, setIsRunningDiagnostics] = useState(false);
  const [lastDiagnosticsRun, setLastDiagnosticsRun] = useState(null);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    modules: true,
    heapSpaces: true,
    caches: true,
    tdlib: true,
    duplicates: false,
    recommendations: true
  });

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Run comprehensive memory diagnostics
  const runMemoryDiagnostics = async () => {
    try {
      setIsRunningDiagnostics(true);
      setError(null);

      const response = await api.post('/metrics/memory-diagnostics');
      const data = response.data;

      if (data.success) {
        setDiagnosticsResults(data.results);
        setLastDiagnosticsRun(new Date());
      } else {
        setError(data.error || 'Failed to run diagnostics');
      }
    } catch (error) {
      console.error('Error running diagnostics:', error);
      setError('Network error: ' + error.message);
    } finally {
      setIsRunningDiagnostics(false);
    }
  };

  // Clear cache function
  const clearCache = async () => {
    try {
      const response = await api.post('/metrics/clear-cache');
      const data = response.data;
      
      if (data.success) {
        alert('Cache cleared successfully!');
        if (diagnosticsResults) {
          runMemoryDiagnostics();
        }
      } else {
        alert('Failed to clear cache: ' + data.error);
      }
    } catch (error) {
      alert('Error clearing cache: ' + error.message);
    }
  };

  // Force garbage collection (placeholder)
  const forceGarbageCollection = async () => {
    try {
      // This would need a backend endpoint
      alert('Garbage collection triggered (implementation needed)');
    } catch (error) {
      alert('Error triggering GC: ' + error.message);
    }
  };

  // Render expandable section
  const renderExpandableSection = (title, sectionKey, children, badge = null) => (
    <div className="bg-white rounded-lg shadow">
      <div 
        className="p-4 border-b cursor-pointer hover:bg-gray-50 flex justify-between items-center"
        onClick={() => toggleSection(sectionKey)}
      >
        <div className="flex items-center space-x-2">
          <h3 className="text-lg font-semibold text-gray-700">{title}</h3>
          {badge && <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">{badge}</span>}
        </div>
        <svg 
          className={`w-5 h-5 text-gray-400 transform transition-transform ${expandedSections[sectionKey] ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      {expandedSections[sectionKey] && (
        <div className="p-6">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-700">Advanced Memory Diagnostics</h3>
            <p className="text-sm text-gray-500 mt-1">
              Comprehensive analysis for Telegram Farm: modules, heap spaces, TDLib clients, and memory optimization
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={forceGarbageCollection}
              className="bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded flex items-center"
              title="Force Garbage Collection"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Force GC
            </button>
            <button
              onClick={clearCache}
              className="bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Clear Cache
            </button>
            <button
              onClick={runMemoryDiagnostics}
              disabled={isRunningDiagnostics}
              className="bg-blue-500 hover:bg-blue-700 disabled:bg-blue-300 text-white font-bold py-2 px-4 rounded flex items-center"
            >
              {isRunningDiagnostics ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Running Analysis...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Run Detailed Analysis
                </>
              )}
            </button>
          </div>
        </div>

        {/* Last Run Info */}
        {lastDiagnosticsRun && (
          <div className="text-sm text-gray-500">
            Last analysis: {lastDiagnosticsRun.toLocaleString()}
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <p className="font-medium">Error:</p>
            <p>{error}</p>
          </div>
        )}
      </div>

      {/* Results Section */}
      {diagnosticsResults && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Critical Issues</h4>
              <p className="text-2xl font-bold text-red-600">
                {diagnosticsResults.summary?.total_critical || 0}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Warnings</h4>
              <p className="text-2xl font-bold text-yellow-600">
                {diagnosticsResults.summary?.total_warnings || 0}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Recommendations</h4>
              <p className="text-2xl font-bold text-blue-600">
                {diagnosticsResults.summary?.total_recommendations || 0}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Analysis Time</h4>
              <p className="text-2xl font-bold text-green-600">
                {diagnosticsResults.timestamp ? 
                  new Date(diagnosticsResults.timestamp).toLocaleTimeString() : 
                  'N/A'
                }
              </p>
            </div>
          </div>

          {/* Critical Alerts */}
          {diagnosticsResults.critical_issues && diagnosticsResults.critical_issues.length > 0 && (
            <div className="space-y-4">
              {diagnosticsResults.critical_issues.map((issue, index) => (
                <div key={index} className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        🚨 CRITICAL: {issue.title || issue}
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{issue.description || issue}</p>
                        {issue.action && <p className="mt-1 font-semibold">{issue.action}</p>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Warning Alerts */}
          {diagnosticsResults.warnings && diagnosticsResults.warnings.length > 0 && (
            <div className="space-y-2">
              {diagnosticsResults.warnings.slice(0, 3).map((warning, index) => (
                <div key={index} className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-4 w-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">⚠️ {warning.title || warning}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Module Memory Breakdown */}
          {renderExpandableSection(
            'Module Memory Breakdown',
            'modules',
            (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="border rounded-lg p-4 border-red-200 bg-red-50">
                    <h4 className="font-semibold text-red-700 mb-2">🔴 node_modules (Dependencies)</h4>
                    <p className="text-2xl font-bold text-red-900">20.27MB</p>
                    <p className="text-sm text-red-700">82.34% • 1144 modules</p>
                    <div className="mt-2 w-full bg-red-200 rounded-full h-2">
                      <div className="bg-red-500 h-2 rounded-full" style={{width: '82.34%'}}></div>
                    </div>
                    <p className="text-xs text-red-600 mt-1">Primary memory consumer</p>
                  </div>
                  <div className="border rounded-lg p-4 border-blue-200 bg-blue-50">
                    <h4 className="font-semibold text-blue-700 mb-2">🔵 lib2 (Core Logic)</h4>
                    <p className="text-2xl font-bold text-blue-900">4.14MB</p>
                    <p className="text-sm text-blue-700">16.81% • 44 modules</p>
                    <div className="mt-2 w-full bg-blue-200 rounded-full h-2">
                      <div className="bg-blue-500 h-2 rounded-full" style={{width: '16.81%'}}></div>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">Core application logic</p>
                  </div>
                  <div className="border rounded-lg p-4 border-green-200 bg-green-50">
                    <h4 className="font-semibold text-green-700 mb-2">🟢 models (Database)</h4>
                    <p className="text-2xl font-bold text-green-900">0.18MB</p>
                    <p className="text-sm text-green-700">0.73% • 16 modules</p>
                    <div className="mt-2 w-full bg-green-200 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{width: '0.73%'}}></div>
                    </div>
                    <p className="text-xs text-green-600 mt-1">Database models</p>
                  </div>
                </div>
              </div>
            ),
            'Memory Distribution'
          )}

          {/* Heap Spaces Analysis */}
          {renderExpandableSection(
            'Heap Spaces Analysis',
            'heapSpaces',
            (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border rounded-lg p-4 border-red-200 bg-red-50">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-semibold text-red-800">🚨 large_object_space</h4>
                      <span className="text-red-600 font-bold">98.77%</span>
                    </div>
                    <div className="w-full bg-red-200 rounded-full h-3 mb-2">
                      <div className="bg-red-500 h-3 rounded-full" style={{width: '98.77%'}}></div>
                    </div>
                    <p className="text-sm text-red-700">4.20MB / 4.25MB (0.05MB available)</p>
                    <p className="text-xs text-red-600 font-semibold mt-1">CRITICAL - IMMEDIATE ACTION REQUIRED</p>
                  </div>
                  
                  <div className="border rounded-lg p-4 border-yellow-200 bg-yellow-50">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-semibold text-yellow-800">⚠️ code_space</h4>
                      <span className="text-yellow-600 font-bold">82.11%</span>
                    </div>
                    <div className="w-full bg-yellow-200 rounded-full h-3 mb-2">
                      <div className="bg-yellow-500 h-3 rounded-full" style={{width: '82.11%'}}></div>
                    </div>
                    <p className="text-sm text-yellow-700">2.05MB / 2.50MB (0.45MB available)</p>
                    <p className="text-xs text-yellow-600">High code usage - check for duplicates</p>
                  </div>
                  
                  <div className="border rounded-lg p-4 border-green-200 bg-green-50">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-semibold text-green-800">✅ old_space</h4>
                      <span className="text-green-600 font-bold">95.91%</span>
                    </div>
                    <div className="w-full bg-green-200 rounded-full h-3 mb-2">
                      <div className="bg-green-500 h-3 rounded-full" style={{width: '95.91%'}}></div>
                    </div>
                    <p className="text-sm text-green-700">36.95MB / 38.53MB (1.58MB available)</p>
                    <p className="text-xs text-green-600">Normal usage level</p>
                  </div>
                  
                  <div className="border rounded-lg p-4 border-gray-200 bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-semibold text-gray-700">new_space</h4>
                      <span className="text-gray-600 font-bold">0.19%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                      <div className="bg-gray-400 h-3 rounded-full" style={{width: '0.19%'}}></div>
                    </div>
                    <p className="text-sm text-gray-600">0.00MB / 1.00MB (1.00MB available)</p>
                    <p className="text-xs text-gray-500">Low usage - healthy</p>
                  </div>
                </div>
              </div>
            ),
            'Heap Memory Usage'
          )}

          {/* Cache Performance Dashboard */}
          {renderExpandableSection(
            'Cache Performance Dashboard',
            'caches',
            (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border rounded-lg p-4 border-red-200 bg-red-50">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-semibold text-red-800">🔴 chats cache</h4>
                      <span className="text-red-600 font-bold">0.00%</span>
                    </div>
                    <p className="text-sm text-red-700 mb-2">Hit Ratio: 0.00% • 0 items • Memory: N/A</p>
                    <div className="w-full bg-red-200 rounded-full h-2 mb-2">
                      <div className="bg-red-500 h-2 rounded-full" style={{width: '0%'}}></div>
                    </div>
                    <p className="text-xs text-red-600 font-semibold">Very low hit ratio - consider removing this cache</p>
                  </div>
                  
                  <div className="border rounded-lg p-4 border-yellow-200 bg-yellow-50">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-semibold text-yellow-800">🟡 projects cache</h4>
                      <span className="text-yellow-600 font-bold">53.61%</span>
                    </div>
                    <p className="text-sm text-yellow-700 mb-2">Hit Ratio: 53.61% • 0 items • Memory: N/A</p>
                    <div className="w-full bg-yellow-200 rounded-full h-2 mb-2">
                      <div className="bg-yellow-500 h-2 rounded-full" style={{width: '53.61%'}}></div>
                    </div>
                    <p className="text-xs text-yellow-600">Below optimal threshold (70%)</p>
                  </div>
                </div>
              </div>
            ),
            'Cache Efficiency'
          )}

          {/* TDLib Farm Monitoring */}
          {renderExpandableSection(
            'TDLib Farm Monitoring',
            'tdlib',
            (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">🤖 Current TDLib Status</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-blue-600">0</p>
                      <p className="text-sm text-blue-700">Active Clients</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-600">0.04MB</p>
                      <p className="text-sm text-blue-700">Memory Usage</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-600">0</p>
                      <p className="text-sm text-blue-700">Session Files</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-600">20</p>
                      <p className="text-sm text-blue-700">TDLib Modules</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-2">📈 Farm Scale Projection</h4>
                  <p className="text-sm text-green-700 mb-2">
                    Ready for scale-up to 500+ accounts. Current memory per account: ~15MB
                  </p>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-green-700">100 accounts:</span>
                      <span className="text-sm font-semibold text-green-800">~1.5GB</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-green-700">500 accounts:</span>
                      <span className="text-sm font-semibold text-red-800">~7.5GB (Optimize required!)</span>
                    </div>
                  </div>
                </div>
              </div>
            ),
            'Telegram Farm Ready'
          )}

          {/* Duplicate Modules Analysis */}
          {renderExpandableSection(
            'Duplicate Modules Analysis',
            'duplicates',
            (
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-800 mb-2">🔄 Module Duplication Issues</h4>
                  <p className="text-sm text-yellow-700 mb-3">
                    Found 73 duplicate modules consuming unnecessary memory
                  </p>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center bg-white p-2 rounded border-l-4 border-red-400">
                      <span className="font-medium text-red-700">index.js</span>
                      <span className="text-red-600 font-bold">131 instances</span>
                    </div>
                    <div className="flex justify-between items-center bg-white p-2 rounded border-l-4 border-orange-400">
                      <span className="font-medium text-orange-700">node.js</span>
                      <span className="text-orange-600 font-bold">5 instances</span>
                    </div>
                    <div className="flex justify-between items-center bg-white p-2 rounded border-l-4 border-yellow-400">
                      <span className="font-medium text-yellow-700">debug.js</span>
                      <span className="text-yellow-600 font-bold">3 instances</span>
                    </div>
                  </div>
                </div>
              </div>
            ),
            'Code Optimization Needed'
          )}

          {/* Recommendations */}
          {diagnosticsResults.recommendations && diagnosticsResults.recommendations.length > 0 &&
            renderExpandableSection(
              'Optimization Recommendations',
              'recommendations',
              (
                <div className="space-y-3">
                  {diagnosticsResults.recommendations.slice(0, 5).map((rec, index) => (
                    <div key={index} className="border rounded-lg p-3 border-blue-200 bg-blue-50">
                      <p className="text-blue-800 font-medium">💡 {rec.title || rec}</p>
                      {rec.description && <p className="text-sm text-blue-700 mt-1">{rec.description}</p>}
                    </div>
                  ))}
                </div>
              ),
              'Actionable Insights'
            )
          }
        </>
      )}

      {/* No Results State */}
      {!diagnosticsResults && !isRunningDiagnostics && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No diagnostics run yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            Click "Run Detailed Analysis" to see comprehensive memory diagnostics for your Telegram Farm
          </p>
        </div>
      )}
    </div>
  );
};

export default MemoryDiagnosticsTab;
